import { useMandate } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";
import { MandateFormViewModel } from "../ViewModels/MandateFormViewModel";
import { useMandateEnricher } from "./useMandateEnricher";
import { MandateValidationSchema } from "../MandateFormValidation";
import { MandateModel } from "../Models";

/**
 * useMandateForm hook – manages contact form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises contact form data by fetching current contact and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current contact (if editing) and the list of agents
 * - Automatically switches between `createMandate` or `updateMandate` based on contact presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const useMandateForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { property, need } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Mandate;
    /**
     * Contact domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const mandateHook = useMandate();

    /**
     * Contact enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        userMap,
        needMap,
        contactMap,
        propertyMap,
        enricherResponse,
        getEnrichedMandate,
    } = useMandateEnricher();

    /**
         * Contact-related enums exposed to the UI layer.
         * Used to build the form view model.
         */
    const { MandateEnums } = useAppEnum();


    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            mandateHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: mandateHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: mandateHook,
        buildVM: (currentEntity: MandateModel) => {
            return new MandateFormViewModel({
                viewEnums: MandateEnums,
                contactMap,
                userMap,
                propertyMap,
                needMap,
                enrichedMandate: getEnrichedMandate(currentEntity),
                need,
                property
            })
        }
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: MandateEnums,
        ValidationSchema: MandateValidationSchema(MandateEnums),
    }
}